/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./DonationRecordRecurring.css";
import { simpleFetch } from "../../restWrapper";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { NumericFormat } from "react-number-format";
import { Helmet } from "react-helmet";
import { getOrganizationID, validatePlatformMatch, resolveThemeColors } from "../../assets/helper";
import { PLATFORM } from "../../config";

class DonationRecordRecurring extends Component {
  constructor() {
    super();
    const subdomain = getOrganizationID(window.location.href).subdomain;
    const domain = getOrganizationID(window.location.href).domain;
    const isCustomDomain = getOrganizationID(window.location.href).isCustomDomain;
    this.state = {
      fetching: false,
      subdomain: subdomain,
      domain: domain,
      isCustomDomain: isCustomDomain,
      buttonColor: "",
      headerColor: "",
      orgId: "",
      orgName: "",
      logo: "",
      paymentRecords: [],
      lastId: 0,
      hasReauthenticateTransactions: false,
      frequencyMenuOpen: false,
    };
    this.fetchData = this.fetchData.bind(this);
    this.checkReauthenticateTransactions = this.checkReauthenticateTransactions.bind(this);
    this.toggleFrequencyMenu = this.toggleFrequencyMenu.bind(this);
  }
  componentDidMount() {
    simpleFetch("/donors/organization", "GET", {
      subDomain: this.state.isCustomDomain ? this.state.domain : this.state.subdomain,
      isCustomDomain: this.state.isCustomDomain,
    }).then((res) => {
      // Validate platform matches organization type
      const platformMatch = validatePlatformMatch(
        res.body.organizationObj.organizationType,
        PLATFORM
      );

      if (!platformMatch) {
        // console.error("Cannot display recurring donations: platform mismatch");
        this.props.history.push({ pathname: "/" + this.props.language });
        return;
      }

      this.setState({
        ...this.state,
        ...{
          buttonColor: res.body.organizationObj.buttonColor,
          headerColor: res.body.organizationObj.headerColor,
          orgId: res.body.id,
          orgName: res.body.organizationObj.organizationName,
          logo: res.body.organizationObj.logo,
        },
      }, () => {
        // Callback ensures orgId is set before fetching data (React 18 fix)
        this.fetchData();
        this.checkReauthenticateTransactions();
      });
    });
  }

  fetchData() {
    if (this.state.fetching) return false;
    if (!this.state.orgId) return false;
    this.setState({
      ...this.state,
      ...{
        fetching: true,
      },
    });

    let findParams = {
      organizationViewId: this.state.orgId,
      status: "scheduled",
    };

    if (this.state.lastId) findParams.lastId = this.state.lastId;

    simpleFetch("/donors/payments", "GET", findParams).then((res) => {
      this.setState({
        ...this.state,
        ...{
          fetching: false,
        },
      });
      //    console.log(res.body);

      if (res.body.length === 0) {
        this.setState({
          ...this.state,
          ...{
            hasMore: false,
          },
        });
        return false;
      }
      const lastItem = res.body[res.body.length - 1];
      this.setState({
        lastId: lastItem.id,
        hasMore: true,
        paymentRecords: this.state.paymentRecords.concat(res.body),
      });
    });
  }

  checkReauthenticateTransactions() {
    if (!this.state.orgId) return false;

    let findParams = {
      organizationViewId: this.state.orgId,
      status: "reauthenticate",
      lastId: 0,
    };

    simpleFetch("/donors/payments", "GET", findParams).then((res) => {
      // If there are any reauthenticate transactions, show the tab
      if (res.body && res.body.length > 0) {
        this.setState({
          hasReauthenticateTransactions: true,
        });
      }
    });
  }

  toggleFrequencyMenu() {
    this.setState((prev) => ({
      frequencyMenuOpen: !prev.frequencyMenuOpen,
    }));
  }

  renderRecord(func, data) {
    const itemKey = "paymentID" + data.id;
    const rDate = new Date(data.scheduledDate);

    const d = rDate.getDate();
    const m = rDate.getMonth() + 1;
    const y = rDate.getFullYear();
    const h = rDate.getHours();
    const min = (rDate.getMinutes() < 10 ? "0" : "") + rDate.getMinutes();

    const date = y + "/" + m + "/" + d + " " + h + ":" + min;

    function onClickRecord() {
      func.props.history.push({
        pathname: "/DonationInfoRecurring",
        state: {
          buttonColor: func.state.buttonColor,
          headerColor: func.state.headerColor,
          orgName: func.state.orgName,
          paymentId: data.id,
        },
      });
    }

    const languageObj = this.props.languageObj.renderRecord;
    const arrowLabel =
      languageObj.viewDetails ||
      languageObj.details ||
      languageObj.btn ||
      "Details";

    const hasRawHeaderColor =
      typeof this.state.headerColor === "string" &&
      this.state.headerColor.length > 0;
    const resolved = hasRawHeaderColor
      ? resolveThemeColors(this.state.headerColor, this.state.buttonColor)
      : { headerColor: "", buttonColor: "" };
    const hasHeaderColor = hasRawHeaderColor;
    const accentColor = hasHeaderColor
      ? `#${resolved.headerColor}`
      : "#606266";
    const accentColorSoft = hasHeaderColor
      ? `#${resolved.headerColor}1A`
      : "#6062661A";
    const buttonColor =
      resolved.buttonColor && resolved.buttonColor.length > 0
        ? `#${resolved.buttonColor}`
        : accentColor;

    const logoWrapperStyle = {
      "--donation-logo-color": accentColor,
      backgroundColor: accentColor,
    };

    const logoStyle = {
      backgroundImage: "url(" + this.state.logo + ")",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
    };

    // Format frequency label
    const getFrequencyLabel = () => {
      if (!data.isRecurring) return null;

      let label = '';
      if (data.recurringInterval === 'daily') label = languageObj.daily;
      else if (data.recurringInterval === 'weekly') label = languageObj.weekly;
      else if (data.recurringInterval === 'biweekly') label = languageObj.biweekly;
      else if (data.recurringInterval === 'monthly') label = languageObj.monthly;
      else if (data.recurringInterval === 'quarterly') label = languageObj.quarterly;
      else if (data.recurringInterval === 'yearly') label = languageObj.yearly;

      if ((data.recurringInterval === 'monthly' || data.recurringInterval === 'quarterly') && data.recurringDay) {
        if (languageObj.dayUnit) {
          // Chinese format: "第21日"
          label += ` (${languageObj.day}${data.recurringDay}${languageObj.dayUnit})`;
        } else {
          // English format: "Day 21"
          label += ` (${languageObj.day} ${data.recurringDay})`;
        }
      }

      return label;
    };

    const getDurationLabel = () => {
      if (!data.isRecurring) return null;

      if (data.recurringEndDate) {
        return `${languageObj.until} ${new Date(data.recurringEndDate).toLocaleDateString()}`;
      }
      return languageObj.untilCancel;
    };

    return (
      <div
        className="DonationRecordRecurring-frame"
        key={itemKey}
        onClick={onClickRecord}
        style={{
          "--donation-frame-accent": accentColor,
          "--donation-frame-accent-soft": accentColorSoft,
          "--donation-arrow-color": accentColor,
          "--donation-arrow-hover-color": buttonColor,
        }}
      >
        {/* Header with badge and date */}
        <div className="DonationRecordRecurring-cardHeader">
          <div className={`DonationRecordRecurring-recurringBadge ${data.isRecurring ? 'recurring' : 'one-time'}`}>
            {data.isRecurring ? languageObj.recurring : languageObj.oneTime}
          </div>
          <div className="DonationRecordRecurring-dateTime">{date}</div>
        </div>

        {/* Content with logo and info */}
        <div className="DonationRecordRecurring-cardContent">
          <div className="DonationRecordRecurring-logoWrapper" style={logoWrapperStyle}>
            <div className="DonationRecordRecurring-logo" style={logoStyle}></div>
          </div>
          <div className="DonationRecordRecurring-transactionInfo">
            {/* Organization and project title */}
            <div className="DonationRecordRecurring-title">
              {func.state.orgName} - {data.projectName}
            </div>

            {/* Recurring details */}
            {data.isRecurring && (
              <div className="DonationRecordRecurring-details">
                <div className="DonationRecordRecurring-detailItem">
                  <span role="img" aria-label="calendar">🗓️</span>
                  {getFrequencyLabel()}
                </div>
                <div className="DonationRecordRecurring-detailItem">
                  <span role="img" aria-label="duration">♾️</span>
                  {getDurationLabel()}
                </div>
                {data.recurringCount !== undefined && data.recurringCount > 0 && (
                  <div className="DonationRecordRecurring-detailItem">
                    {languageObj.processed} {data.recurringCount} {languageObj.time}
                  </div>
                )}
              </div>
            )}

            {/* Amount */}
            <div className="DonationRecordRecurring-amountRow">
              <div className="DonationRecordRecurring-amount">
                {languageObj.amount}: HK
                <NumericFormat
                  decimalSeparator="."
                  displayType="text"
                  valueIsNumericString={true}
                  value={data.amount}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={"$"}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              </div>
              <div className="DonationRecordRecurring-arrowPill">
                <span>{arrowLabel}</span>
                <div className="DonationRecordRecurring-nextArrow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.headerColor) {
      return null;
    }

    const languageObj = this.props.languageObj;

    const scrollBarStyle = {
      overflow: "inherit",
      marginBottom: "100px",
    };

    const headerStyle = {
      background: "#" + this.state.headerColor,
    };

    return (
      <div className="DonationRecordRecurring-view" data-testid="donationrecordrecurring-view">
        <Helmet>
          <title>{this.state.orgName}</title>
        </Helmet>
        <div className="DonationRecordRecurring-bg" style={headerStyle} />
        <div className="DonationRecordRecurring-container">
          <div className="DonationRecordRecurring-header">
            <Link to="/">
              <div className="DonationRecordRecurring-backContainer">
                <div className="DonationRecordRecurring-back">
                  <div className="DonationRecordRecurring-backBtn"></div>
                  <div className="DonationRecordRecurring-backText">
                    {languageObj.backTxt}
                  </div>
                </div>
              </div>
            </Link>

            <div className={`DonationRecordRecurring-frequencyContainer ${this.state.frequencyMenuOpen ? "is-open" : ""}`}>
              <Link to="/" className="DonationRecordRecurring-frequencyBack">
                <div className="DonationRecordRecurring-frequencyBackIcn" />
                <span>{languageObj.backTxt}</span>
              </Link>
              <button
                type="button"
                className="DonationRecordRecurring-frequencyToggle"
                onClick={this.toggleFrequencyMenu}
                aria-expanded={this.state.frequencyMenuOpen}
                aria-label={languageObj.statusToggle || "Toggle status filter"}
              >
                <div className="DonationRecordRecurring-scheduleIcn" />
                <span>{languageObj.scheduled || "Scheduled"}</span>
                <span className="DonationRecordRecurring-frequencyToggleIcon" />
              </button>
              <div className="DonationRecordRecurring-frequencyCurrent">
                {languageObj.scheduled || "Scheduled"}
              </div>
              <div className="DonationRecordRecurring-frequency">
                <Link
                  to="/DonationRecord"
                  className="DonationRecordRecurring-tickGroup"
                >
                  <div className="DonationRecordRecurring-tickIcn" />
                  <div className="DonationRecordRecurring-tickTxt">
                    {languageObj.completed}
                  </div>
                </Link>

                <div className="DonationRecordRecurring-scheduleGroup">
                  <div className="DonationRecordRecurring-scheduleIcn" />
                  <div className="DonationRecordRecurring-scheduleTxt">
                    {languageObj.scheduled}
                  </div>
                </div>

                <Link
                  to="/DonationRecordReauthenticate"
                  className="DonationRecordRecurring-reauthGroup"
                >
                  <div className="DonationRecordRecurring-reauthIcn" />
                  <div className="DonationRecordRecurring-reauthTxt">
                    {languageObj.reauthenticate}
                  </div>
                </Link>
              </div>
            </div>

            <div className="DonationRecordRecurring-btn">{languageObj.btn}</div>
          </div>

          <div className="DonationRecordRecurring-main">
            <InfiniteScroll
              dataLength={this.state.paymentRecords.length}
              next={this.fetchData}
              hasMore={this.state.hasMore}
              style={scrollBarStyle}
            >
              {this.state.paymentRecords.map((record) =>
                this.renderRecord(this, record),
              )}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    );
  }
}

DonationRecordRecurring.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
};

export default DonationRecordRecurring;
